import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { BarChart3, Calendar, LayoutGrid } from "lucide-react";
import { useAuth } from "../../../components/AuthProvider";
import { createRouteError } from "../../../components/RouteErrorBoundary";
import { PageSkeleton } from "../../../components/RouteSkeleton";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { StackedCards } from "../../../components/ui/stacked-cards";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  type TimeBlockRow,
  useCreateTimeBlock,
  useDeleteTimeBlock,
  useTimeBlocks,
  useUpdateTimeBlock,
} from "../../../hooks/useTimeBlocks";

type TimeBlock = TimeBlockRow;
import type { RouterContextWithSession } from "../../../router";

export const Route = createFileRoute("/_admin/time-management/")({
  beforeLoad: async ({ context }) => {
    if (typeof window === "undefined") return {};
    const sb = context.supabase;
    if (!sb) return {};
    const {
      data: { session },
    } = await sb.auth.getSession();
    if (!session) {
      throw redirect({ to: "/auth" });
    }
    return { session };
  },
  pendingComponent: () => <PageSkeleton rows={6} variant="list" />,
  errorComponent: createRouteError("Time Management"),
  component: TimeManagementPage,
});

const BLOCK_TYPE_LABELS: Record<TimeBlock["block_type"], string> = {
  focus: "Focus",
  meeting: "Meeting",
  break: "Break",
  admin: "Admin",
  personal: "Personal",
};

function TimeManagementPage() {
  const { user, session, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated || !user || !session) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Redirecting to sign-in…</p>
      </div>
    );
  }

  return (
    <AuthenticatedTimeManagement
      userId={user.id}
      accessToken={session.access_token}
    />
  );
}

const MS_PER_DAY = 86_400_000;
const SUNDAY_TO_MONDAY_OFFSET = -6;

function AuthenticatedTimeManagement({
  userId,
  accessToken,
}: {
  userId: string;
  accessToken: string;
}) {
  const auth = { userId, accessToken };
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: timeBlocks, isLoading, error } = useTimeBlocks({
    ...auth,
    allUsers: false,
  });
  const createMutation = useCreateTimeBlock(auth);
  const updateMutation = useUpdateTimeBlock(auth);
  const deleteMutation = useDeleteTimeBlock(auth);

  // Add form state
  const [newTitle, setNewTitle] = useState("");
  const [newBlockType, setNewBlockType] = useState<TimeBlock["block_type"]>(
    "focus"
  );
  const [newStart, setNewStart] = useState("");
  const [newEnd, setNewEnd] = useState("");
  const [newColor, setNewColor] = useState("#3B82F6");

  const handleAdd = async () => {
    if (!newTitle.trim() || !newStart || !newEnd) return;
    if (new Date(newEnd) <= new Date(newStart)) return;
    await createMutation.mutateAsync({
      title: newTitle.trim(),
      block_type: newBlockType,
      start_time: newStart,
      end_time: newEnd,
      color: newColor,
    });
    setNewTitle("");
    setNewBlockType("focus");
    setNewStart("");
    setNewEnd("");
    setNewColor("#3B82F6");
    setShowAddForm(false);
  };

  // Computed stats
  const stats = useMemo(() => {
    if (!timeBlocks || timeBlocks.length === 0) {
      return { blocksToday: 0, minutesToday: 0, blocksThisWeek: 0, mostUsedType: "—" };
    }

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + MS_PER_DAY);

    // Monday of current week
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? SUNDAY_TO_MONDAY_OFFSET : 1 - dayOfWeek;
    const weekStart = new Date(todayStart.getTime() + mondayOffset * MS_PER_DAY);
    const weekEnd = new Date(weekStart.getTime() + 7 * MS_PER_DAY);

    const todayBlocks = timeBlocks.filter((b) => {
      const s = new Date(b.start_time);
      return s >= todayStart && s < todayEnd;
    });

    const minutesToday = todayBlocks.reduce((sum, b) => {
      return sum + Math.round(
        (new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 60_000,
      );
    }, 0);

    const blocksThisWeek = timeBlocks.filter((b) => {
      const s = new Date(b.start_time);
      return s >= weekStart && s < weekEnd;
    }).length;

    // Most common block type
    const typeCounts: Record<string, number> = {};
    for (const b of timeBlocks) {
      typeCounts[b.block_type] = (typeCounts[b.block_type] || 0) + 1;
    }
    const mostUsedType =
      Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";

    return {
      blocksToday: todayBlocks.length,
      minutesToday,
      blocksThisWeek,
      mostUsedType: BLOCK_TYPE_LABELS[mostUsedType as TimeBlock["block_type"]] ?? mostUsedType,
    };
  }, [timeBlocks]);

  const analytics = useMemo(() => {
    if (!timeBlocks || timeBlocks.length === 0) {
      return {
        byType: [] as { type: string; label: string; minutes: number }[],
        last7Days: [] as { date: string; minutes: number }[],
        totalHours: 0,
        mostProductiveDay: "—",
        mostCommonType: "—",
        maxMinutes: 1,
      };
    }

    // Hours by block type
    const typeMinutes: Record<string, number> = {};
    const typeCounts: Record<string, number> = {};
    for (const b of timeBlocks) {
      const mins = Math.round((new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 60_000);
      typeMinutes[b.block_type] = (typeMinutes[b.block_type] ?? 0) + mins;
      typeCounts[b.block_type] = (typeCounts[b.block_type] ?? 0) + 1;
    }
    const blockTypes: TimeBlock["block_type"][] = ["focus", "meeting", "break", "admin", "personal"];
    const byType = blockTypes
      .map((t) => ({ type: t, label: BLOCK_TYPE_LABELS[t], minutes: typeMinutes[t] ?? 0 }))
      .filter((t) => t.minutes > 0)
      .sort((a, b) => b.minutes - a.minutes);

    // Last 7 days
    const now = new Date();
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now);
      d.setDate(now.getDate() - (6 - i));
      d.setHours(0, 0, 0, 0);
      const dEnd = new Date(d.getTime() + 86_400_000);
      const dateStr = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      const minutes = timeBlocks
        .filter((b) => {
          const s = new Date(b.start_time);
          return s >= d && s < dEnd;
        })
        .reduce((sum, b) => {
          return sum + Math.round((new Date(b.end_time).getTime() - new Date(b.start_time).getTime()) / 60_000);
        }, 0);
      return { date: dateStr, minutes };
    });

    const totalMinutes = Object.values(typeMinutes).reduce((a, b) => a + b, 0);
    const totalHours = Math.round((totalMinutes / 60) * 10) / 10;

    const mostProductiveEntry = last7Days.reduce((best, day) => day.minutes > best.minutes ? day : best, last7Days[0]);
    const mostProductiveDay = mostProductiveEntry.minutes > 0 ? mostProductiveEntry.date : "—";

    const mostCommonType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "—";
    const mostCommonTypeLabel = BLOCK_TYPE_LABELS[mostCommonType as TimeBlock["block_type"]] ?? mostCommonType;

    const maxMinutes = Math.max(...byType.map((t) => t.minutes), 1);

    return { byType, last7Days, totalHours, mostProductiveDay, mostCommonType: mostCommonTypeLabel, maxMinutes };
  }, [timeBlocks]);

  return (
    <main className="container mx-auto p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Time Management</h2>
          <p className="text-muted-foreground mt-1">
            Plan your day with time blocks, review stats, and track productivity.
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => setShowAddForm(true)}>+ Add Block</Button>
        )}
      </div>

      <Tabs defaultValue="all-blocks" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all-blocks">
            <LayoutGrid className="mr-2 h-4 w-4" />
            All Blocks
          </TabsTrigger>
          <TabsTrigger value="dashboard">
            <BarChart3 className="mr-2 h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <Calendar className="mr-2 h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* ── All Blocks tab ── */}
        <TabsContent value="all-blocks" className="space-y-4">
          {showAddForm && (
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <h2 className="text-sm font-semibold text-card-foreground">
                New time block
              </h2>
              <Input
                type="text"
                placeholder="Title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Label className="text-xs text-muted-foreground">Type:</Label>
                {(
                  [
                    "focus",
                    "meeting",
                    "break",
                    "admin",
                    "personal",
                  ] as const
                ).map((t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant={newBlockType === t ? "default" : "secondary"}
                    onClick={() => setNewBlockType(t)}
                    className="px-2.5 py-1 text-xs rounded-full capitalize"
                  >
                    {BLOCK_TYPE_LABELS[t]}
                  </Button>
                ))}
              </div>
              <div className="flex gap-3 flex-wrap">
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">
                    Start
                  </Label>
                  <Input
                    type="datetime-local"
                    value={newStart}
                    onChange={(e) => setNewStart(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">
                    End
                  </Label>
                  <Input
                    type="datetime-local"
                    value={newEnd}
                    onChange={(e) => setNewEnd(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground block mb-1">
                    Color
                  </Label>
                  <Input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="h-9 w-14 cursor-pointer"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleAdd}
                  disabled={
                    !newTitle.trim() ||
                    !newStart ||
                    !newEnd ||
                    new Date(newEnd) <= new Date(newStart) ||
                    createMutation.isPending
                  }
                >
                  {createMutation.isPending ? "Adding…" : "Add"}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setShowAddForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
              {error.message}
            </div>
          )}

          {!isLoading && !error && timeBlocks?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-4xl mb-3">📅</span>
              <p className="text-muted-foreground text-sm">
                No time blocks yet. Add your first block to plan your day.
              </p>
            </div>
          )}

          {!isLoading && timeBlocks && timeBlocks.length > 0 && (
            <ul className="space-y-2">
              {timeBlocks.map((block) =>
                editingId === block.id ? (
                  <EditTimeBlockRow
                    key={block.id}
                    block={block}
                    onSave={(updates) => {
                      updateMutation.mutate({ id: block.id, updates });
                      setEditingId(null);
                    }}
                    onCancel={() => setEditingId(null)}
                    isPending={updateMutation.isPending}
                  />
                ) : (
                  <TimeBlockListItem
                    key={block.id}
                    block={block}
                    onEdit={() => setEditingId(block.id)}
                    onDelete={() => deleteMutation.mutate(block.id)}
                    isDeleting={
                      deleteMutation.isPending &&
                      deleteMutation.variables === block.id
                    }
                  />
                ),
              )}
            </ul>
          )}
        </TabsContent>

        {/* ── Dashboard tab ── */}
        <TabsContent value="dashboard" className="space-y-4">
          <StackedCards
            cards={[
              {
                id: 'blocks-today',
                title: 'Blocks Today',
                value: stats.blocksToday,
                icon: <LayoutGrid className="h-4 w-4 text-blue-600" />,
                color: 'border-l-blue-500',
                bgColor: 'bg-blue-100 dark:bg-blue-900/30',
                subtitle: 'time blocks scheduled today',
              },
              {
                id: 'minutes-today',
                title: 'Minutes Today',
                value: stats.minutesToday,
                icon: <Calendar className="h-4 w-4 text-emerald-600" />,
                color: 'border-l-emerald-500',
                bgColor: 'bg-emerald-100 dark:bg-emerald-900/30',
                subtitle: 'total minutes planned',
              },
              {
                id: 'blocks-week',
                title: 'Blocks This Week',
                value: stats.blocksThisWeek,
                icon: <BarChart3 className="h-4 w-4 text-purple-600" />,
                color: 'border-l-purple-500',
                bgColor: 'bg-purple-100 dark:bg-purple-900/30',
                subtitle: 'blocks Mon–Sun',
              },
              {
                id: 'most-used-type',
                title: 'Most Used Type',
                value: stats.mostUsedType,
                icon: <BarChart3 className="h-4 w-4 text-amber-600" />,
                color: 'border-l-amber-500',
                bgColor: 'bg-amber-100 dark:bg-amber-900/30',
                subtitle: 'across all blocks',
              },
            ]}
            variant="auto"
          />

          {/* Recent blocks preview */}
          {!isLoading && timeBlocks && timeBlocks.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Recent Blocks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {timeBlocks.slice(0, 5).map((block) => {
                  const start = new Date(block.start_time);
                  const end = new Date(block.end_time);
                  const mins = Math.round((end.getTime() - start.getTime()) / 60_000);
                  return (
                    <div
                      key={block.id}
                      className="flex items-center gap-3 rounded-md border p-2"
                    >
                      <div
                        className="w-2 h-8 rounded-full flex-shrink-0"
                        style={{ backgroundColor: block.color ?? "#3B82F6" }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{block.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {start.toLocaleDateString(undefined, { dateStyle: "short" })} · {mins} min
                        </p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground capitalize">
                        {BLOCK_TYPE_LABELS[block.block_type]}
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Analytics tab ── */}
        <TabsContent value="analytics" className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Hours Logged</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{analytics.totalHours}h</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Most Productive Day</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold truncate">{analytics.mostProductiveDay}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Top Block Type</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold">{analytics.mostCommonType}</p>
              </CardContent>
            </Card>
          </div>

          {/* Hours by Block Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Hours by Block Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analytics.byType.length === 0 ? (
                <p className="text-sm text-muted-foreground">No data yet. Add some time blocks to see analytics.</p>
              ) : (
                analytics.byType.map((item) => (
                  <div key={item.type} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium capitalize">{item.label}</span>
                      <span className="text-muted-foreground">{Math.round(item.minutes / 60 * 10) / 10}h ({item.minutes}m)</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${Math.round((item.minutes / analytics.maxMinutes) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Last 7 Days */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Last 7 Days</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {analytics.last7Days.map((day) => (
                <div key={day.date} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground w-32">{day.date}</span>
                  <div className="flex-1 mx-3 h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary/70 transition-all"
                      style={{
                        width: day.minutes > 0
                          ? `${Math.round((day.minutes / Math.max(...analytics.last7Days.map((d) => d.minutes), 1)) * 100)}%`
                          : "0%",
                      }}
                    />
                  </div>
                  <span className="text-right w-16 font-medium">{day.minutes > 0 ? `${day.minutes}m` : "—"}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  );
}

function TimeBlockListItem({
  block,
  onEdit,
  onDelete,
  isDeleting,
}: {
  block: TimeBlock;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const start = new Date(block.start_time);
  const end = new Date(block.end_time);
  const durationMins = Math.round(
    (end.getTime() - start.getTime()) / (1000 * 60)
  );

  return (
    <li className="flex items-center gap-3 rounded-lg border bg-card p-3 group hover:shadow-sm transition-shadow">
      <div
        className="flex-shrink-0 w-2 h-12 rounded-full"
        style={{ backgroundColor: block.color ?? "#3B82F6" }}
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground truncate">{block.title}</p>
        <p className="text-xs text-muted-foreground">
          {start.toLocaleString(undefined, {
            dateStyle: "short",
            timeStyle: "short",
          })}{" "}
          –{" "}
          {end.toLocaleString(undefined, {
            timeStyle: "short",
          })}{" "}
          · {durationMins} min
        </p>
      </div>
      <span className="flex-shrink-0 px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground capitalize">
        {BLOCK_TYPE_LABELS[block.block_type]}
      </span>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          title="Edit"
        >
          ✏️
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          disabled={isDeleting}
          title="Delete"
          className="hover:text-destructive hover:bg-destructive/10"
        >
          🗑️
        </Button>
      </div>
    </li>
  );
}

function EditTimeBlockRow({
  block,
  onSave,
  onCancel,
  isPending,
}: {
  block: TimeBlock;
  onSave: (updates: {
    title?: string;
    start_time?: string;
    end_time?: string;
    block_type?: TimeBlock["block_type"];
    color?: string;
  }) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState(block.title);
  const [blockType, setBlockType] = useState<TimeBlock["block_type"]>(
    block.block_type
  );
  const [startTime, setStartTime] = useState(
    block.start_time.slice(0, 16) // datetime-local format
  );
  const [endTime, setEndTime] = useState(block.end_time.slice(0, 16));
  const [color, setColor] = useState(block.color ?? "#3B82F6");

  const handleSave = () => {
    onSave({
      title: title.trim(),
      block_type: blockType,
      start_time: startTime ? new Date(startTime).toISOString() : undefined,
      end_time: endTime ? new Date(endTime).toISOString() : undefined,
      color,
    });
  };

  return (
    <li className="rounded-lg border-2 border-primary bg-card p-3 space-y-2">
      <Input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title"
      />
      <div className="flex flex-wrap items-center gap-2">
        {(["focus", "meeting", "break", "admin", "personal"] as const).map(
          (t) => (
            <Button
              key={t}
              size="sm"
              variant={blockType === t ? "default" : "secondary"}
              onClick={() => setBlockType(t)}
              className="px-2 py-0.5 text-xs rounded-full capitalize"
            >
              {BLOCK_TYPE_LABELS[t]}
            </Button>
          ))}
      </div>
      <div className="flex gap-3 flex-wrap">
        <Input
          type="datetime-local"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
          className="w-auto"
        />
        <Input
          type="datetime-local"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
          className="w-auto"
        />
        <Input
          type="color"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          className="h-8 w-12 cursor-pointer"
        />
      </div>
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || isPending}
        >
          {isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </li>
  );
}
